import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Search, MoreHorizontal, Eye, Edit3, Plus, Key, UserX,
  FolderOpen, RefreshCw, Loader2, X, Mail, ChevronLeft, ChevronRight,
  CheckCircle2, AlertCircle, LogIn
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';

const formatNumber = (num) => new Intl.NumberFormat('es-ES').format(num || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PLAN_COLORS = {
  free: 'bg-gray-500/20 text-gray-400',
  founder: 'bg-amber-500/20 text-amber-400',
  creador: 'bg-brand/20 text-brand',
  agencia: 'bg-purple-500/20 text-purple-400',
  enterprise: 'bg-amber-500/20 text-amber-400'
};

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400',
  inactive: 'bg-red-500/20 text-red-400'
};

const AdminUsuarios = () => {
  const { api, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Forms
  const [editForm, setEditForm] = useState({});
  const [creditsForm, setCreditsForm] = useState({ amount: 0, reason: '' });
  const [tempPassword, setTempPassword] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: usersPage.toString(), limit: '20' });
      if (searchTerm) params.append('search', searchTerm);
      if (planFilter !== 'all') params.append('plan', planFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, [api, usersPage, searchTerm, planFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewUser = async (userId) => {
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setSelectedUser(res.data);
      setShowUserModal(true);
    } catch (err) {
      toast.error('Error cargando usuario');
    }
  };

  const handleEditUser = (user) => {
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      agency_name: user.agency_name || '',
      plan: user.plan || 'free'
    });
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleImpersonateUser = async (user) => {
    try {
      const response = await api.post(`/admin/users/${user.id}/impersonate`);
      if (response.data.access_token) {
        // Store original admin token for returning later
        const currentToken = localStorage.getItem('access_token');
        localStorage.setItem('admin_original_token', currentToken);
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('impersonated_user', JSON.stringify({ id: user.id, email: user.email }));
        
        // Set new token and reload
        localStorage.setItem('access_token', response.data.access_token);
        toast.success(`Entrando como ${user.email}...`);
        
        // Redirect to dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      }
    } catch (err) {
      toast.error('Error al entrar como usuario');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}`, editForm);
      toast.success('Usuario actualizado');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error('Error actualizando usuario');
    }
  };

  const handleOpenCreditsModal = (user) => {
    setSelectedUser(user);
    setCreditsForm({ amount: 0, reason: '' });
    setShowCreditsModal(true);
  };

  const handleAddCredits = async () => {
    if (!creditsForm.amount || creditsForm.amount === 0) {
      toast.error('Introduce una cantidad');
      return;
    }
    try {
      await api.post(`/admin/users/${selectedUser.id}/add-credits`, creditsForm);
      toast.success(`${creditsForm.amount > 0 ? '+' : ''}${creditsForm.amount} créditos aplicados`);
      setShowCreditsModal(false);
      fetchUsers();
    } catch (err) {
      toast.error('Error añadiendo créditos');
    }
  };

  const handleOpenPasswordModal = (user) => {
    setSelectedUser(user);
    setTempPassword('');
    setShowPasswordModal(true);
  };

  const handleResetPassword = async () => {
    try {
      const res = await api.post(`/admin/users/${selectedUser.id}/reset-password`);
      setTempPassword(res.data.temp_password);
      toast.success('Contraseña reseteada');
    } catch (err) {
      toast.error('Error reseteando contraseña');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('¿Seguro que quieres desactivar este usuario?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuario desactivado');
      fetchUsers();
    } catch (err) {
      toast.error('Error desactivando usuario');
    }
  };

  const totalPages = Math.ceil(usersTotal / 20);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-usuarios-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center gap-3">
              <Users className="w-7 h-7 text-brand" />
              Gestión de Usuarios
            </h1>
            <p className="text-text-muted mt-1">{formatNumber(usersTotal)} usuarios registrados</p>
          </div>
          <Button onClick={fetchUsers} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="pl-10 bg-surface border-border-subtle"
              data-testid="user-search-input"
            />
          </div>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="h-10 px-3 rounded-lg bg-surface border border-border-subtle text-text-main text-sm"
            data-testid="plan-filter"
          >
            <option value="all">Todos los planes</option>
            <option value="free">Free</option>
            <option value="founder">Founder</option>
            <option value="creador">Creador</option>
            <option value="agencia">Agencia</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-lg bg-surface border border-border-subtle text-text-main text-sm"
            data-testid="status-filter"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-app-bg/50">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Usuario</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Email</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Plan</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Créditos</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Funnels</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Registro</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-text-muted">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-t border-border-subtle hover:bg-surface-hover">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-brand/20 flex items-center justify-center text-brand font-medium">
                          {(user.name || user.email || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-text-main">{user.name || 'Sin nombre'}</p>
                          {user.agency_name && (
                            <p className="text-xs text-text-muted">{user.agency_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-text-main">{user.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${PLAN_COLORS[user.plan] || PLAN_COLORS.free}`}>
                        {user.plan || 'free'}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-text-main font-medium">{formatNumber(user.credits_balance)}</td>
                    <td className="p-4 text-sm text-text-main">{user.funnel_count || 0}</td>
                    <td className="p-4 text-sm text-text-muted">{formatDate(user.created_at)}</td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-surface border-border-subtle">
                          <DropdownMenuItem onClick={() => handleImpersonateUser(user)}>
                            <LogIn className="w-4 h-4 mr-2" /> Entrar como usuario
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleViewUser(user.id)}>
                            <Eye className="w-4 h-4 mr-2" /> Ver detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit3 className="w-4 h-4 mr-2" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenCreditsModal(user)}>
                            <Plus className="w-4 h-4 mr-2" /> Añadir créditos
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenPasswordModal(user)}>
                            <Key className="w-4 h-4 mr-2" /> Reset contraseña
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeactivateUser(user.id)} className="text-red-400">
                            <UserX className="w-4 h-4 mr-2" /> Desactivar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Mostrando {((usersPage - 1) * 20) + 1}-{Math.min(usersPage * 20, usersTotal)} de {usersTotal}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                disabled={usersPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm text-text-main">
                {usersPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUsersPage(p => Math.min(totalPages, p + 1))}
                disabled={usersPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="bg-surface border-border-subtle max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-text-main flex items-center gap-2">
              <Users className="w-5 h-5" />
              Detalle de Usuario
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="bg-app-bg">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="credits">Créditos</TabsTrigger>
                <TabsTrigger value="funnels">Funnels</TabsTrigger>
              </TabsList>
              <TabsContent value="info" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Nombre</p>
                    <p className="text-text-main font-medium">{selectedUser.name || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Email</p>
                    <p className="text-text-main">{selectedUser.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Agencia</p>
                    <p className="text-text-main">{selectedUser.agency_name || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Plan</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${PLAN_COLORS[selectedUser.plan]}`}>
                      {selectedUser.plan}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Registro</p>
                    <p className="text-text-main">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">Último acceso</p>
                    <p className="text-text-main">{formatDate(selectedUser.last_login)}</p>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="credits" className="mt-4 space-y-4">
                <div className="flex items-center justify-between p-4 bg-app-bg rounded-lg">
                  <div>
                    <p className="text-xs text-text-muted">Saldo actual</p>
                    <p className="text-2xl font-bold text-brand">{formatNumber(selectedUser.credits_balance)}</p>
                  </div>
                  <Button onClick={() => handleOpenCreditsModal(selectedUser)}>
                    <Plus className="w-4 h-4 mr-2" /> Añadir créditos
                  </Button>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-text-main">Últimos movimientos</p>
                  {(selectedUser.recent_transactions || []).slice(0, 10).map((tx) => (
                    <div key={tx.id || `utx-${tx.created_at}-${tx.amount}`} className="flex items-center justify-between p-2 bg-app-bg rounded-lg">
                      <span className="text-sm text-text-main">{tx.description || tx.type}</span>
                      <span className={`text-sm font-medium ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="funnels" className="mt-4 space-y-2">
                {(selectedUser.recent_funnels || []).map((funnel) => (
                  <div key={funnel.id || funnel.name} className="flex items-center justify-between p-2 bg-app-bg rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-text-main">{funnel.name}</p>
                      <p className="text-xs text-text-muted">{funnel.client_name}</p>
                    </div>
                    <span className="text-xs text-text-muted">{formatDate(funnel.created_at)}</span>
                  </div>
                ))}
                {(!selectedUser.recent_funnels || selectedUser.recent_funnels.length === 0) && (
                  <p className="text-sm text-text-muted text-center py-4">Sin funnels</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-text-muted">Nombre</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-app-bg border-border-subtle mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-text-muted">Email</label>
              <Input
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-app-bg border-border-subtle mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-text-muted">Nombre agencia</label>
              <Input
                value={editForm.agency_name}
                onChange={(e) => setEditForm({ ...editForm, agency_name: e.target.value })}
                className="bg-app-bg border-border-subtle mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-text-muted">Plan</label>
              <select
                value={editForm.plan}
                onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                className="w-full h-10 px-3 rounded-lg bg-app-bg border border-border-subtle text-text-main mt-1"
              >
                <option value="free">Free</option>
                <option value="founder">Founder</option>
                <option value="creador">Creador</option>
                <option value="agencia">Agencia</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
              <Button onClick={handleSaveEdit} className="bg-brand">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Añadir Créditos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-text-muted">Usuario: <span className="text-text-main">{selectedUser?.email}</span></p>
            <div>
              <label className="text-sm text-text-muted">Cantidad (+ o -)</label>
              <Input
                type="number"
                value={creditsForm.amount}
                onChange={(e) => setCreditsForm({ ...creditsForm, amount: parseInt(e.target.value) || 0 })}
                className="bg-app-bg border-border-subtle mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-text-muted">Motivo</label>
              <Textarea
                value={creditsForm.reason}
                onChange={(e) => setCreditsForm({ ...creditsForm, reason: e.target.value })}
                placeholder="Motivo del ajuste..."
                className="bg-app-bg border-border-subtle mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreditsModal(false)}>Cancelar</Button>
              <Button onClick={handleAddCredits} className="bg-brand">Aplicar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Resetear Contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-text-muted">Usuario: <span className="text-text-main">{selectedUser?.email}</span></p>
            {tempPassword ? (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-400 mb-2">Contraseña temporal generada:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-app-bg rounded text-text-main font-mono">{tempPassword}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword);
                      toast.success('Copiado');
                    }}
                  >
                    Copiar
                  </Button>
                </div>
                <p className="text-xs text-text-muted mt-2">Envía esta contraseña al usuario por un canal seguro.</p>
              </div>
            ) : (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  Se generará una contraseña temporal que el usuario deberá cambiar en su primer acceso.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cerrar</Button>
              {!tempPassword && (
                <Button onClick={handleResetPassword} className="bg-brand">Generar contraseña</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default AdminUsuarios;
